import type { AnyAction } from '@reduxjs/toolkit';
import type { CSSProperties, MouseEvent, ReactElement } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useGameConfig } from '../../../config/GameConfigProvider';
import { useCashierCallbackConnected } from '../../../hooks/useCashierCallbackConnected';
import { useExitCallbackConnected } from '../../../hooks/useExitCallbackConnected';
import { useIsLobbyButtonHiddenConnected } from '../../../hooks/useIsLobbyButtonHiddenConnected';
import { useShouldStayConnected } from '../../../hooks/useShouldStayConnected';
import { Logger } from '../../../logger/Logger';
import { useCoreDispatch } from '../../../store/CoreStore';
import { useChatSlice } from '../../../store/slices/chat/selectors';
import { chatActions } from '../../../store/slices/chat/slice';
import { ChatTabType } from '../../../store/slices/chat/types';
import { useConfigSlice } from '../../../store/slices/config/selectors';
import { useHeaderSlice } from '../../../store/slices/header/selectors';
import { headerActions } from '../../../store/slices/header/slice';
import { popupActions } from '../../../store/slices/popup/slice';
import { useModifiers } from '../../../styles/hooks/useModifiers';
import { BackButton } from '../../controls/backButton/BackButton';
import { IconButton, Icons } from '../../controls/iconButton/IconButton';
import { Overscrollable } from '../../overscrollable';
import { PopupType } from '../../popup/types';
import { L12nText } from '../../text/L12nText';
import { NavPages, NavPagesConnected } from '../navPages/NavPagesConnected';
import { NavPagesTitleConnected } from '../navPages/NavPagesTitleConnected';

import { NavDrawerButton } from './navDrawerButton/NavDrawerButton';

import styles from './navDrawer.module.css';

export const NavDrawerConnected = (): ReactElement => {
  const dispatch = useCoreDispatch();
  const cashierCallback = useCashierCallbackConnected();
  const shouldStayAtTable = useShouldStayConnected();
  const { quickGuideContent } = useGameConfig();
  const exitCallback = useExitCallbackConnected();
  const isLobbyButtonHidden = useIsLobbyButtonHiddenConnected();

  const exitUrl = useConfigSlice.casinoConfig()?.exitUrl;
  const isChatDisabled = useConfigSlice.isChatDisabled();
  const isExpanded = useHeaderSlice.getNavExpanded();
  const isForcedExpanded = useHeaderSlice.getNavForcedExpanded();
  const isLiveSupportRead = useChatSlice.getIsLiveSupportRead();
  const isLobbyOpen = useHeaderSlice.getIsMiniLobbyOpen();
  const navRouteLevel = useHeaderSlice.getNavRouteLevel();
  const open = useHeaderSlice.getNavOpen();
  const routeByLevel = useHeaderSlice.getNavRouteByLevel(2);

  const [delta, setDelta] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const deltaRef = useRef<number | null>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const handleWrapperRef = useRef<HTMLDivElement>(null);
  const hasMovedRef = useRef(false);
  const originRef = useRef<number | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const forcedOpen = useMemo(() => {
    return routeByLevel === NavPages.QUICK_GUIDE;
  }, [routeByLevel]);

  useEffect(() => {
    if (open) {
      setDelta(null);
    }
  }, [open]);

  useEffect(() => {
    if (forcedOpen) {
      dispatch(headerActions.setNavExpanded(true));
    }
  }, [dispatch, forcedOpen]);

  const mods = useModifiers(styles, 'navDrawer', {
    open,
    overlaid: navRouteLevel >= 2,
    expanded: isExpanded,
    forcedExpanded: isForcedExpanded,
    isDragging,
  });

  const handleBack = (event: MouseEvent): void => {
    event.stopPropagation();

    dispatch(headerActions.popNavRoute());
  };

  const handleClose = (event: MouseEvent | undefined): void => {
    if (event) {
      event.stopPropagation();
    }
    dispatch(headerActions.setNavRoute([]));
  };

  useEffect(() => {
    const handleWrapperElement = handleWrapperRef.current;
    if (!handleWrapperElement) return;
    if (isForcedExpanded) return;

    const handlePointerDown = (event: PointerEvent): void => {
      originRef.current = event.clientY;
      setDelta(0);

      deltaRef.current = 0;
      setIsDragging(true);

      document.addEventListener('pointermove', handleMove, { passive: true });
      document.addEventListener('pointerup', handlePointerUp, { passive: true });
    };

    const handleMove = (event: PointerEvent): void => {
      if (originRef.current === null) return;
      const newDelta = originRef.current - event.clientY;
      if (isExpanded && newDelta > 0) {
        setDelta(0);
      } else if (!isExpanded && newDelta < 0) {
        setDelta(newDelta);
      } else {
        setDelta(newDelta);
      }
      deltaRef.current = newDelta;
      hasMovedRef.current = true;
    };

    const handlePointerUp = (): void => {
      const deltaValue = deltaRef.current;
      if (deltaValue !== null) {
        if ((isExpanded && deltaValue < -30) || (!isExpanded && deltaValue > 30)) {
          dispatch(headerActions.setNavExpanded(deltaValue > 0));
          setDelta(null);
        } else if (!isExpanded && deltaValue < -30) {
          dispatch(headerActions.setNavRoute([]));
        } else if (!hasMovedRef.current || Math.abs(deltaValue) < 4) {
          dispatch(headerActions.setNavExpanded(!isExpanded));
          setDelta(null);
        } else {
          setDelta(null);
        }
      }
      setIsDragging(false);

      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', handlePointerUp);

      originRef.current = null;
      deltaRef.current = null;
      hasMovedRef.current = false;
    };

    const handleTouchStart = (event: TouchEvent): void => {
      event.stopPropagation();
      event.preventDefault();
    };

    handleWrapperElement.addEventListener('touchstart', handleTouchStart);
    handleWrapperElement.addEventListener('pointerdown', handlePointerDown);

    return () => {
      handleWrapperElement.removeEventListener('touchstart', handleTouchStart);
      handleWrapperElement.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [dispatch, isExpanded, isForcedExpanded]);

  const lobbyButtonClick = (e: MouseEvent<HTMLElement>): void => {
    e.preventDefault();
    e.stopPropagation();

    if (!isLobbyOpen) {
      Logger.logEvent('click', 'openLobbyMobMenu', Date.now(), 1, '', false);
      shouldStayAtTable
        ? dispatch(popupActions.pushPopup(PopupType.DontLeaveRoundInProgress))
        : dispatch(headerActions.setMiniLobbyOpen(true));
    }
  };

  const curtainMods = useModifiers(styles, 'navDrawer__curtain', { open });

  return (
    <>
      <div
        className={`${styles.navDrawer__curtain} ${curtainMods}`}
        onClick={(): AnyAction => dispatch(headerActions.setNavRoute([]))}
      />
      <div className={`${styles.navDrawer__wrapper} ${open ? styles.navDrawer__wrapper_open : ''}`} ref={wrapperRef}>
        <div
          data-testid="nav-drawer"
          className={`${styles.navDrawer} ${mods}`}
          style={
            {
              '--delta': delta ? `${delta}px` : undefined,
            } as CSSProperties
          }
          ref={drawerRef}
        >
          <div className={`${styles.navDrawer__header}`}>
            <div className={`${styles.navDrawer__headerHandleWrapper}`} ref={handleWrapperRef}>
              <div data-testid="drawer-handle" className={`${styles.navDrawer__headerHandle}`} />
            </div>
            <div
              className={`${styles.navDrawer__headerActionContainer} ${
                navRouteLevel < 2 ? styles.navDrawer__headerActionContainer_hidden : ''
              }`}
            >
              <BackButton
                data-testid="drawer-back-button"
                onClick={handleBack}
                visible={navRouteLevel >= 2}
                className={`${styles.navDrawer__headerAction}`}
              />
            </div>
            <div className={`${styles.navDrawer__headerTitle}`}>
              <NavPagesTitleConnected />
            </div>
            <div className={styles.navDrawer__headerActionContainer}>
              <IconButton
                data-testid="drawer-close-button"
                small
                ghost
                icon="Close"
                onClick={handleClose}
                className={`${styles.navDrawer__headerClose}`}
              />
            </div>
          </div>
          <div className={styles.navDrawer__container}>
            <div className={styles.navDrawer__home}>
              <Overscrollable>
                <div className={`${styles.navDrawer__grid}`}>
                  {!isLobbyButtonHidden && (
                    <NavDrawerButton
                      data-testid="drawer-lobby-button"
                      icon={Icons.Grid2x2}
                      onClick={lobbyButtonClick}
                      lobby
                      revealed={open}
                    >
                      <L12nText tk="LOBBY" />
                    </NavDrawerButton>
                  )}

                  {cashierCallback && (
                    <NavDrawerButton data-testid="cashier-button" icon={Icons.Cashier} onClick={cashierCallback}>
                      <L12nText tk="CASHIER" />
                    </NavDrawerButton>
                  )}

                  {isChatDisabled ? null : (
                    <NavDrawerButton
                      data-testid="drawer-chat-button"
                      icon={Icons.Chat}
                      onClick={(): void => {
                        Logger.logEvent('click', 'openChatMobMenu', Date.now(), 1, '', false);
                        dispatch(chatActions.setSelectedTab(ChatTabType.GameChat));
                        dispatch(headerActions.pushNavRoute(NavPages.CHAT));
                      }}
                    >
                      <L12nText tk="CHAT" />
                    </NavDrawerButton>
                  )}

                  <NavDrawerButton
                    data-testid="drawer-history-button"
                    icon={Icons.History}
                    onClick={(): void => {
                      Logger.logEvent('click', 'openHistoryMobMenu', Date.now(), 1, '', false);
                      dispatch(headerActions.pushNavRoute(NavPages.HISTORY));
                    }}
                  >
                    <L12nText tk="HISTORY" />
                  </NavDrawerButton>

                  {quickGuideContent && (
                    <NavDrawerButton
                      data-testid="drawer-quick-guide-button"
                      icon={Icons.LightBulb}
                      onClick={(): void => {
                        Logger.logEvent('click', 'openQuickGuideMobMenu', Date.now(), 1, '', false);
                        dispatch(headerActions.pushNavRoute(NavPages.QUICK_GUIDE));
                      }}
                    >
                      <L12nText tk="QG_QUICK_GUIDE" />
                    </NavDrawerButton>
                  )}

                  <NavDrawerButton
                    data-testid="drawer-bet-limits-button"
                    icon={Icons.Payouts}
                    onClick={(): void => {
                      Logger.logEvent('click', 'openBetLimitsMobMenu', Date.now(), 1, '', false);
                      dispatch(headerActions.pushNavRoute(NavPages.BET_LIMITS));
                    }}
                  >
                    <L12nText tk="MENU_PAYOUTS_LIMITS" />
                  </NavDrawerButton>

                  <NavDrawerButton
                    data-testid="drawer-settings-button"
                    icon={Icons.Settings}
                    onClick={(): void => {
                      Logger.logEvent('click', 'openSettingsMobMenu', Date.now(), 1, '', false);
                      dispatch(headerActions.pushNavRoute(NavPages.SETTINGS));
                    }}
                  >
                    <L12nText tk="SETTINGS" />
                  </NavDrawerButton>

                  <NavDrawerButton
                    data-testid="drawer-help-button"
                    icon={Icons.Help}
                    onClick={(): void => {
                      Logger.logEvent('click', 'openHelpMobMenu', Date.now(), 1, '', false);
                      dispatch(headerActions.pushNavRoute(NavPages.HELP));
                    }}
                  >
                    <L12nText tk="HELP" />
                  </NavDrawerButton>

                  {isChatDisabled ? null : (
                    <NavDrawerButton
                      data-testid="drawer-live-support-button"
                      icon={Icons.Support}
                      onClick={(): void => {
                        Logger.logEvent('click', 'openSupportChatMobMenu', Date.now(), 1, '', false);
                        dispatch(chatActions.setSelectedTab(ChatTabType.LiveSupportChat));
                        dispatch(headerActions.pushNavRoute(NavPages.CHAT));
                      }}
                      showBadge={!isLiveSupportRead}
                    >
                      <L12nText tk="MENU_SUPPORT" />
                    </NavDrawerButton>
                  )}

                  {exitUrl && (
                    <NavDrawerButton
                      data-testid="drawer-home-button"
                      icon={Icons.Home}
                      onClick={(): void => {
                        Logger.logEvent('click', 'openHomeMobMenu', Date.now(), 1, '', false);
                        if (shouldStayAtTable) {
                          dispatch(popupActions.pushPopup(PopupType.DontLeaveRoundInProgress));
                          return;
                        }
                        exitCallback();
                        window.location.href = exitUrl;
                      }}
                    >
                      <L12nText tk="HOME" />
                    </NavDrawerButton>
                  )}
                </div>
              </Overscrollable>
            </div>
            <div className={`${styles.navDrawer__pages}`}>
              <NavPagesConnected />
            </div>
          </div>
          <div className={styles.navDrawer__footer}></div>
        </div>
      </div>
    </>
  );
};
